// src/hooks/useDocumentMeta.ts
//
// Client-side per-route <head> metadata for the SPA: document title, meta
// description, OG/Twitter tags, and the canonical URL. This covers real
// browsers (tab titles, history, in-page SEO signals for JS-rendering
// crawlers); non-JS link unfurlers are covered server-side by the
// netlify/edge-functions/product-meta.js rewrite.
//
// Deliberately dependency-free (no react-helmet): the tags already exist in
// public/index.html, so this just updates them in place.

import { useEffect } from "react";

const SITE_NAME = "Americana & Company";
const BASE_URL = "https://americanaandcompany.com";

const setMetaContent = (selector: string, content: string) => {
  const el = document.head.querySelector<HTMLMetaElement>(selector);
  if (el) el.setAttribute("content", content);
};

export interface DocumentMeta {
  /** Page title; rendered as "<title> | Americana & Company". Empty = site name only. */
  title?: string;
  description?: string;
  /** Absolute image URL for link previews. Omit to keep the site default. */
  image?: string;
  /** Path for the canonical/og:url (defaults to the current pathname). */
  path?: string;
}

export const useDocumentMeta = ({
  title,
  description,
  image,
  path,
}: DocumentMeta) => {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    document.title = fullTitle;
    setMetaContent('meta[property="og:title"]', fullTitle);
    setMetaContent('meta[name="twitter:title"]', fullTitle);

    if (description) {
      setMetaContent('meta[name="description"]', description);
      setMetaContent('meta[property="og:description"]', description);
      setMetaContent('meta[name="twitter:description"]', description);
    }

    if (image) {
      setMetaContent('meta[property="og:image"]', image);
      setMetaContent('meta[name="twitter:image"]', image);
    }

    const url = `${BASE_URL}${path ?? window.location.pathname}`;
    setMetaContent('meta[property="og:url"]', url);
    const canonical = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    if (canonical) canonical.setAttribute("href", url);
  }, [title, description, image, path]);
};
